import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    AiDispositionParsedDataType,
    AiDispositionPayloadType,
    AiDispositionTemplateResultType,
} from "@callveriq/type/AiDisposition";

export const aiDispositionApiSlice = createApi({
    reducerPath: "aiDispositionApiSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    tagTypes: ["Templates"],
    endpoints: (builder) => ({
        getDispositionTemplateList: builder.query<
            AiDispositionTemplateResultType[],
            { isArchive: boolean }
        >({
            query: (payload) => ({
                url: "/gpt/ai_disposition/",
                method: "GET",
                params: { is_archived: payload.isArchive ? "true" : "false" },
            }),
            providesTags: [{ type: "Templates", id: "ALL" }],
        }),
        getDispositionTemplateById: builder.query<
            AiDispositionTemplateResultType,
            number
        >({
            query: (id) => ({
                url: `/gpt/ai_disposition/${id}/`,
                method: "GET",
            }),
        }),
        getDispositionTemplateFiltersById: builder.query<
            Pick<
                AiDispositionTemplateResultType,
                "id" | "name" | "disposition_scope" | "dispositions"
            >,
            number
        >({
            query: (id) => ({
                url: `gpt/ai_disposition/filters/${id}/`,
                method: "GET",
            }),
        }),
        archiveDispositionTemplate: builder.mutation<
            AiDispositionTemplateResultType,
            { is_archived: boolean; id: number; is_disabled?: boolean }
        >({
            query: ({ id, ...payload }) => ({
                url: `/gpt/ai_disposition/${id}/`,
                method: "PATCH",
                body: payload,
            }),
            invalidatesTags: [{ type: "Templates", id: "ALL" }],
        }),
        createDispositionTemplate: builder.mutation<
            AiDispositionTemplateResultType,
            AiDispositionPayloadType
        >({
            query: (payload) => ({
                url: "/gpt/ai_disposition/",
                method: "POST",
                body: payload,
            }),
        }),
        updateDispositionTemplate: builder.mutation<
            AiDispositionTemplateResultType,
            { payload: FormData; id: number }
        >({
            query: ({ id, payload }) => ({
                url: `/gpt/ai_disposition/${id}/`,
                method: "PATCH",
                headers: {
                    "Content-Type": "multipart/form-data;",
                },
                body: payload,
            }),
        }),
        updateDispositionTemplateStatus: builder.mutation<
            AiDispositionTemplateResultType,
            { id: number; is_disabled: boolean }
        >({
            query: ({ id, is_disabled }) => ({
                url: `/gpt/ai_disposition/${id}/`,
                method: "PATCH",
                body: { is_disabled },
            }),
            onQueryStarted: async (payload, { queryFulfilled, dispatch }) => {
                const patchResult = dispatch(
                    aiDispositionApiSlice.util.updateQueryData(
                        "getDispositionTemplateList",
                        { isArchive: false },
                        (draft) =>
                            draft.map((e) =>
                                e.id === payload.id
                                    ? { ...e, is_disabled: payload.is_disabled }
                                    : e
                            )
                    )
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        validateDispositionTemplate: builder.mutation<
            { validate: true; dispositions: AiDispositionParsedDataType },
            FormData
        >({
            query: (payload) => ({
                url: "/gpt/ai_disposition/",
                method: "POST",
                headers: {
                    "Content-Type": "multipart/form-data;",
                },
                params: { validate: "true" },
                body: payload,
            }),
        }),
        getDispositionByTemplateId: builder.query<
            Array<{ id: number; name: string }>,
            number
        >({
            query: (id) => ({
                url: `/gpt/ai_disposition/level/template/${id}/`,
                method: "GET",
            }),
        }),
        getLevelsByDispositionId: builder.query<
            Array<{ id: number; name: string }>,
            number
        >({
            query: (id) => ({
                url: `/gpt/ai_disposition/level/disposition/${id}/`,
                method: "GET",
            }),
        }),
        deleteDispositionTemplate: builder.mutation<void, number>({
            query: (id) => ({
                url: `/gpt/ai_disposition/${id}/`,
                method: "DELETE",
            }),
            invalidatesTags: [{ type: "Templates", id: "ALL" }],
        }),
    }),
});

export const {
    useGetDispositionTemplateListQuery,
    useArchiveDispositionTemplateMutation,
    useGetDispositionTemplateByIdQuery,
    useCreateDispositionTemplateMutation,
    useValidateDispositionTemplateMutation,
    useUpdateDispositionTemplateMutation,
    useGetDispositionTemplateFiltersByIdQuery,
    useGetDispositionByTemplateIdQuery,
    useGetLevelsByDispositionIdQuery,
    useUpdateDispositionTemplateStatusMutation,
    useDeleteDispositionTemplateMutation,
} = aiDispositionApiSlice;
